import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { getCalendarEvents, getCalendarSettings } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Clock, ChevronDown, ChevronUp, Church, ArrowRight, Megaphone } from 'lucide-react';
import usePageBanner from '../hooks/usePageBanner';
import { AutoTranslate, useAutoTranslate } from '../hooks/useAutoTranslate';
import AddToCalendarButton from '../components/AddToCalendarButton';
import { createSanitizedMarkup } from '../utils/sanitize';

// Renders a mass/event description as sanitized HTML with auto-translation.
const EventDescriptionHtml = ({ description, className = '' }) => {
  const translated = useAutoTranslate(description || '');
  if (!description) return null;
  return (
    <div
      className={className}
      dangerouslySetInnerHTML={createSanitizedMarkup(translated || description)}
    />
  );
};

// Card-based MassCard matching EventCard style from EventsPage
const MassCard = ({ event, t, isRTL, language }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const startDate = new Date(event.start_datetime);
  // Always use 24-hour format regardless of language
  const timeStr = startDate.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', hour12: false });

  // Check if description needs expansion (more than 100 chars of visible text)
  const descriptionText = (event.description || '').replace(/<[^>]*>/g, '');
  const needsExpansion = descriptionText.length > 100;

  return (
    <Card className="border-0 shadow-md hover:shadow-xl card-hover transition-shadow" data-testid={`mass-${event.uid}`}>
      <CardContent className="p-4">
        <div className={`flex items-start justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 text-lg">
              <AutoTranslate>{event.title}</AutoTranslate>
            </h4>
            {event.description && (
              <div className="mt-1">
                <EventDescriptionHtml
                  description={event.description}
                  className={`text-sm text-gray-600 ${!isExpanded && needsExpansion ? 'line-clamp-2' : ''}`}
                />
                {needsExpansion && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`mt-1 text-xs font-medium text-yellow-600 hover:text-yellow-700 flex items-center gap-1 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    {isExpanded ? (
                      <>
                        {t('showLess') || 'Visa mindre'}
                        <ChevronUp className="w-3 h-3" />
                      </>
                    ) : (
                      <>
                        {t('more') || 'mer...'}
                        <ChevronDown className="w-3 h-3" />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            <div className={`flex flex-wrap gap-3 mt-3 text-sm text-gray-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Clock className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'} text-yellow-600`} />
                <span>{timeStr}</span>
                <AddToCalendarButton event={event} isRTL={isRTL} />
              </div>
              {event.language && (
                <div className="flex items-center">
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">
                    {event.language}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MassDayGroup = ({ date, masses, t, isPast, isRTL, language }) => {
  const [year, month, day] = date.split('-').map(Number);
  const dateObj = new Date(Date.UTC(year, month - 1, day));
  const localeMap = {
    'sv': 'sv-SE', 'en': 'en-US', 'ar': 'ar-SA',
    'pl': 'pl-PL', 'es': 'es-ES', 'fr': 'fr-FR', 'de': 'de-DE'
  };
  const locale = localeMap[language] || 'sv-SE';
  const dayName = dateObj.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' });
  const dateStr = dateObj.toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' });

  const now = new Date();
  const todayUTC = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
  const isToday = date === todayUTC;

  // Sort masses by time
  const sortedMasses = [...masses].sort((a, b) => 
    new Date(a.start_datetime) - new Date(b.start_datetime)
  );

  return (
    <div className="mb-6">
      <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'} mb-4 pb-2 border-b-2 ${isToday ? 'border-green-500' : isPast ? 'border-gray-300' : 'border-yellow-400'}`}>
        <Calendar className={`w-5 h-5 ${isToday ? 'text-green-600' : isPast ? 'text-gray-400' : 'text-yellow-600'}`} />
        <h3 className={`text-lg font-serif font-bold capitalize ${isPast ? 'text-gray-500' : 'text-gray-900'}`}>
          {dayName}, {dateStr}
          {isToday && <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm font-normal text-green-600 bg-green-100 px-2 py-0.5 rounded`}>{t('today') || 'Idag'}</span>}
          {isPast && <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm font-normal text-gray-400`}>({t('passed') || 'passerat'})</span>}
        </h3>
      </div>
      <div className="space-y-2">
        {sortedMasses.map((event, idx) => (
          <MassCard key={event.uid || idx} event={event} t={t} isRTL={isRTL} language={language} />
        ))}
      </div>
    </div>
  );
};

const MassCalendar = ({ church, t, isRTL, language, weekOffset }) => {
  const [data, setData] = useState({ masses: [], events: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        const result = await getCalendarEvents(church, weekOffset);
        setData(result);
      } catch (error) {
        console.error('Error loading events:', error);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [church, weekOffset]);

  const groupMassesByDate = (masses) => {
    const grouped = {};
    masses.forEach(event => {
      const date = event.start_datetime.split('T')[0];
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(event);
    });
    return grouped;
  };

  const getWeekDates = (offset) => {
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const utcDay = today.getUTCDay();
    const pythonWeekday = utcDay === 0 ? 6 : utcDay - 1;
    const weekStart = new Date(today);
    weekStart.setUTCDate(today.getUTCDate() - pythonWeekday + (offset * 7));
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setUTCDate(weekStart.getUTCDate() + i);
      dates.push(`${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`);
    }
    return dates;
  };

  const massesOnly = data.masses || [];
  const groupedByDate = groupMassesByDate(massesOnly);
  const weekDates = getWeekDates(weekOffset);

  return (
    <div>
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loadingCalendar')}</p>
        </div>
      ) : (
        <div>
          {weekDates
            .filter(date => groupedByDate[date] && groupedByDate[date].length > 0)
            .map(date => {
              const now = new Date();
              const todayUTC = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
              const isPast = date < todayUTC;
              return (
                <MassDayGroup
                  key={date}
                  date={date}
                  masses={groupedByDate[date]}
                  t={t}
                  isPast={isPast}
                  isRTL={isRTL}
                  language={language}
                />
              );
            })}
          {weekDates.filter(date => groupedByDate[date] && groupedByDate[date].length > 0).length === 0 && (
            <Card className="p-8 text-center border-0 shadow-xl card-hover">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">
                {t('noEventsThisWeek')}
              </h3>
              <p className="text-gray-600">
                {t('selectAnotherWeek')}
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

const LiturgiPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [searchParams] = useSearchParams();
  const churchParam = searchParams.get('church');
  const savedChurch = localStorage.getItem('preferredChurch');
  const initialChurch = churchParam || savedChurch || 'karlskrona';

  const [activeChurch, setActiveChurch] = useState(initialChurch);
  const [settings, setSettings] = useState({ weeks_ahead: 2 });
  const [weekOffset, setWeekOffset] = useState(0);
  const { bannerImage } = usePageBanner('events', language);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getCalendarSettings();
        setSettings(data);
      } catch (error) {
        console.error('Error loading calendar settings:', error);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (churchParam && ['karlskrona', 'karlshamn', 'olofstrom'].includes(churchParam)) {
      setActiveChurch(churchParam);
      localStorage.setItem('preferredChurch', churchParam);
    }
  }, [churchParam]);

  const handleChurchChange = (churchId) => {
    setActiveChurch(churchId);
    localStorage.setItem('preferredChurch', churchId);
  };

  const churches = [
    { id: 'karlskrona', nameKey: 'karlskrona' },
    { id: 'karlshamn', nameKey: 'karlshamn' },
    { id: 'olofstrom', nameKey: 'olofstrom' }
  ];

  const weeksAhead = settings.weeks_ahead || 2;
  const weekOptions = [];
  const localeMap = {
    'sv': 'sv-SE', 'en': 'en-US', 'ar': 'ar-SA',
    'pl': 'pl-PL', 'es': 'es-ES', 'fr': 'fr-FR', 'de': 'de-DE'
  };
  const locale = localeMap[language] || 'sv-SE';
  
  for (let i = 0; i <= weeksAhead; i++) {
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const utcDay = today.getUTCDay();
    const pythonWeekday = utcDay === 0 ? 6 : utcDay - 1;
    const weekStart = new Date(today);
    weekStart.setUTCDate(today.getUTCDate() - pythonWeekday + (i * 7));
    const weekEnd = new Date(weekStart);
    weekEnd.setUTCDate(weekStart.getUTCDate() + 6);

    let label = '';
    if (i === 0) label = t('currentWeekLabel') || 'Denna vecka';
    else if (i === 1) label = t('nextWeekLabel') || 'Nästa vecka';
    else label = `${i} ${t('weeksAhead') || 'veckor'}`;

    const startStr = `${weekStart.getUTCDate()} ${weekStart.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' })}`;
    const endStr = `${weekEnd.getUTCDate()} ${weekEnd.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' })}`;
    weekOptions.push({ value: i, label: `${label} (${startStr} - ${endStr})` });
  }

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img src={bannerImage} alt="Liturgi" className="w-full h-full object-cover" />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-center`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${isRTL ? 'text-right' : ''}`}>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              <AutoTranslate>Liturgi</AutoTranslate>
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              <AutoTranslate>Mässor och gudstjänster i våra kyrkor</AutoTranslate>
            </p>
          </div>
        </div>
      </section>

      {/* Calendar Content */}
      <section className="py-12" data-testid="liturgi-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeChurch} onValueChange={handleChurchChange} className="w-full">
            <div className={`flex flex-col gap-3 mb-6`}>
              {/* Church Tabs - Full width on mobile */}
              <TabsList className="grid grid-cols-3 h-auto w-full">
                {churches.map((church) => (
                  <TabsTrigger
                    key={church.id}
                    value={church.id}
                    className="py-2 px-2 sm:px-3 text-xs sm:text-sm font-medium data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900"
                    data-testid={`liturgi-tab-${church.id}`}
                  >
                    <Church className={`w-4 h-4 hidden sm:block ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {t(church.nameKey)}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Week Selector - Full width on mobile */}
              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'}`}>
                <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div className="relative flex-1 sm:flex-initial">
                  <select
                    value={weekOffset}
                    onChange={(e) => setWeekOffset(parseInt(e.target.value))}
                    className={`appearance-none w-full sm:w-auto bg-white border border-gray-300 rounded-lg px-3 py-2 ${isRTL ? 'pl-8' : 'pr-8'} text-gray-900 font-medium focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 cursor-pointer text-sm`}
                    data-testid="liturgi-week-selector"
                  >
                    {weekOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <ChevronDown className={`absolute ${isRTL ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none`} />
                </div>
              </div>
            </div>

            {churches.map((church) => (
              <TabsContent key={church.id} value={church.id}>
                <MassCalendar
                  church={church.id}
                  t={t}
                  isRTL={isRTL}
                  language={language}
                  weekOffset={weekOffset}
                />
              </TabsContent>
            ))}
          </Tabs>

          {/* Navigation to Events/Meddelanden */}
          <div className={`mt-8 pt-6 border-t border-gray-200 ${isRTL ? 'text-right' : ''}`}>
            <Link 
              to="/events" 
              className={`inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg shadow-md transition-all hover:shadow-lg ${isRTL ? 'flex-row-reverse' : ''}`}
              data-testid="nav-to-meddelanden"
            >
              <Megaphone className="w-5 h-5" />
              <AutoTranslate>Se meddelanden</AutoTranslate>
              <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LiturgiPage;
